import {
  GraphQLServerUrl,
  RxDatabase,
  RxReplicationWriteToMasterRow,
  WithDeleted,
} from 'rxdb'
import { replicateGraphQL } from 'rxdb/plugins/replication-graphql'

import { graphql } from '@/gql'
import {
  createQueryWithHash,
  pullResponseModifier,
} from '@/system/rxdb/replications/utils.ts'
import { AssessmentMeasurement } from '@/system/rxdb/schemas/assessmentMeasurement'

type PullData =
  | {
      nodes: Array<WithDeleted<AssessmentMeasurement>>
    }
  | { assessmentMeasurement: WithDeleted<AssessmentMeasurement> }

// amount of items we want to pull max for each iteration
const BATCH_SIZE = 20

export const ASSESSMENT_MEASUREMENT_FRAGMENT = /* GraphQL*/ `
    fragment assessmentMeasurement on AssessmentMeasurement {
        rowId
        updatedAt
        deleted
        assessmentId
        organization {
          arrayValues {
            fieldName
            value
          }
          stringValues {
            fieldName
            value
          }
        }
        mobility {
          arrayValues {
            fieldName
            value
          }
          stringValues {
            fieldName
            value
          }
        }
        mentalHealth {
          arrayValues {
            fieldName
            value
          }
          stringValues {
            fieldName
            value
          }
        }
        physicalHealth {
          arrayValues {
            fieldName
            value
          }
          stringValues {
            fieldName
            value
          }
        }
        selfSufficiency {
          arrayValues {
            fieldName
            value
          }
          stringValues {
            fieldName
            value
          }
        }
        socialContacts {
          arrayValues {
            fieldName
            value
          }
          stringValues {
            fieldName
            value
          }
        }
        livingEnvironment {
          arrayValues {
            fieldName
            value
          }
          stringValues {
            fieldName
            value
          }
        }
        careRelationship {
          arrayValues {
            fieldName
            value
          }
          stringValues {
            fieldName
            value
          }
        }
        relativeSituation {
          arrayValues {
            fieldName
            value
          }
          stringValues {
            fieldName
            value
          }
        }
   }
`
export const createAssessmentMeasurementReplication = (
  db: RxDatabase,
  url: GraphQLServerUrl,
  autoStart: boolean
) => {
  const assessmentMeasurementQuery = createQueryWithHash(
    graphql(/* GraphQL */ `
      query assessmentMeasurementDocuments(
        $checkpoint: DocumentCheckpointInput
        $first: Int!
      ) {
        assessmentMeasurementDocuments(checkpoint: $checkpoint, first: $first) {
          nodes {
            ...assessmentMeasurement
          }
        }
      }
    `)
  )
  const assessmentMeasurementStreamQuery = createQueryWithHash(
    graphql(/* GraphQL */ `
      subscription streamAssessmentMeasurements {
        streamAssessmentMeasurements {
          assessmentMeasurement {
            ...assessmentMeasurement
          }
        }
      }
    `)
  )
  const assessmentMeasurementPushQuery = createQueryWithHash(
    graphql(/* GraphQL*/ `
      mutation addAssessmentMeasurement($rows: [AssessmentMeasurementInput]!) {
        assessmentMeasurementReplicate(input: { items: $rows }) {
          result {
            ...assessmentMeasurement
          }
        }
      }
    `)
  )
  return replicateGraphQL({
    collection: db.assessment_measurement,
    url,
    credentials: 'include',
    replicationIdentifier: url.http || '',
    autoStart,
    deletedField: 'deleted',
    pull: {
      includeWsHeaders: true,
      streamQueryBuilder: () => {
        return {
          ...assessmentMeasurementStreamQuery,
          variables: {},
        }
      },
      responseModifier: (data: PullData, origin) => {
        // in case of subscriptions, we always only receive a single assignment, so we map it here into the right
        // expected structure
        if (origin === 'stream' && data && 'assessmentMeasurement' in data) {
          return pullResponseModifier({
            nodes: [data.assessmentMeasurement],
          })
        }
        return pullResponseModifier<AssessmentMeasurement>(
          data as {
            nodes: Array<WithDeleted<AssessmentMeasurement>>
          }
        )
      },
      batchSize: BATCH_SIZE,
      queryBuilder: (checkpoint, limit) => {
        return {
          ...assessmentMeasurementQuery,
          variables: {
            checkpoint,
            first: limit,
          },
        }
      },
    },
    push: {
      responseModifier: (data: { result: AssessmentMeasurement[] }) => {
        return data.result
      },
      queryBuilder(
        rows: RxReplicationWriteToMasterRow<AssessmentMeasurement>[]
      ) {
        return {
          ...assessmentMeasurementPushQuery,
          variables: {
            rows: rows.map((row) => row.newDocumentState),
          },
        }
      },
    },
  })
}
